import { inject, Injectable } from "@angular/core";
import JSZip from "jszip";
import { concatMap, from, map, Observable, switchMap, toArray } from "rxjs";
import { SubjectModelLocationsSchema } from "../../../_offline/models/subjectModel-locations.schema";
import { SubjectModelSchema } from "../../../_offline/models/subjectModel.schema";
import { SubjectModelDbService } from "../../database/subject-model-db.service";
import { SubjectModelLocationsDbService } from "../../database/subject-model-locations-db.service";
import { FileSystemAccessService } from "../../file-system-access.service";
import { AssetModuleClient } from "../asset-module-client.service";
import { Log } from "./log.decorator";
import { toAssetTypeExport } from "./subject-model-converter";
import { AssetTypeExport, AssetTypeStructureImage, IdsMap } from "./types";

@Injectable({
  providedIn: "root"
})
export class SubjectModelMigration {
  private subjectModelDbService: SubjectModelDbService = inject(SubjectModelDbService);
  private subjectModelLocationsDbService: SubjectModelLocationsDbService = inject(SubjectModelLocationsDbService);
  private assetModuleClient: AssetModuleClient = inject(AssetModuleClient);
  private fileSystemAccessService: FileSystemAccessService = inject(FileSystemAccessService);

  @Log("migrateSubjectModels")
  public migrateSubjectModels(): Observable<IdsMap> {
    return this.getSubjectModelData().pipe(
      switchMap(({ subjectModels, allLocations }) =>
        from(subjectModels).pipe(concatMap((subjectModel) => this.migrateSubjectModel(subjectModel, allLocations)))
      ),
      toArray(),
      map((ids) => new IdsMap(ids.map(({ oldId, newId }) => [oldId, newId])))
    );
  }

  @Log("migrateSubjectModel", { writeArguments: false })
  private migrateSubjectModel(subjectModel: SubjectModelSchema, allLocations: SubjectModelLocationsSchema[]) {
    const locations = allLocations.filter((l) => l.subjectModelId === subjectModel.id);
    const payload = toAssetTypeExport(subjectModel, locations, subjectModel.structImageFilePath);
    return this.importData(subjectModel, payload.export, payload.structImageEntries);
  }

  @Log("importAssetType", { writeArguments: false })
  private importData(subjetModel: SubjectModelSchema, payload: AssetTypeExport, structImageEntries: AssetTypeStructureImage[]) {
    return from(this.buildZip(payload, structImageEntries)).pipe(
      concatMap((zipFile) => this.assetModuleClient.importAssetType(zipFile)),
      map((assetTypeId) => ({ oldId: subjetModel.id, newId: assetTypeId }))
    );
  }

  private getSubjectModelData() {
    return this.subjectModelDbService
      .getAllOnPremise()
      .pipe(
        switchMap((subjectModels) => this.subjectModelLocationsDbService.getAll().pipe(map((allLocations) => ({ subjectModels, allLocations }))))
      );
  }

  private async buildZip(exportObj: AssetTypeExport, structImageEntries: AssetTypeStructureImage[]): Promise<File> {
    const zip = new JSZip();
    zip.file("assetTypes.json", JSON.stringify(exportObj));

    for (const entry of structImageEntries) {
      const normalizedPath = entry.SourceFilePath.replace(/\//g, FileSystemAccessService.PATH_SEPARATOR);
      const file = await this.fileSystemAccessService.getFile(normalizedPath, entry.FileName, entry.ContentType);
      zip.file(`assetTypeImages/${entry.FileName}`, file);
    }

    const blob = await zip.generateAsync({ type: "blob" });
    return new File([blob], "assetType.zip", { type: "application/zip" });
  }
}
